// src/components/QuickIntakeForm.jsx
import React, { useState, useEffect, useRef } from 'react';
import ReCAPTCHA from 'react-google-recaptcha';

// Helper: parent URL (iframe or fallback)
const getParentUrl = () => document.referrer || window.location.href;

const SITE_KEY_V2 = process.env.REACT_APP_RECAPTCHA_SITE_KEY_V2;
const API_URL     = process.env.REACT_APP_API_URL;

export default function QuickIntakeForm() {
  const [form, setForm]           = useState({
    first_name: '',
    middle_name: '',
    last_name: '',
    phone: '',
    email: '',
    message: '',
  });
  const [status, setStatus]       = useState('');
  const [captchaToken, setCaptchaToken] = useState(null);
  const [isLoading, setIsLoading]   = useState(false);
  const [submitted, setSubmitted]   = useState(false);
  const [showPhoneConsent, setShowPhoneConsent] = useState(false);
  const containerRef = useRef(null);
  const phoneConsentRef = useRef(null);

  // Format phone as (123) 456-7890
  const formatPhone = (value) => {
    const digits = value.replace(/\D/g, '');
    if (digits.length === 0) return '';
    if (digits.length < 4) return `(${digits}`;
    if (digits.length < 7) return `(${digits.slice(0,3)}) ${digits.slice(3)}`;
    return `(${digits.slice(0,3)}) ${digits.slice(3,6)}-${digits.slice(6,10)}`;
  };

  useEffect(() => {
    const sendHeight = () => {
      if (!containerRef.current) return;
      const height = containerRef.current.scrollHeight;
      window.parent.postMessage({ type: 'resize-iframe', height }, '*');
    };
    sendHeight();
    const ro = new ResizeObserver(sendHeight);
    ro.observe(containerRef.current);
    return () => ro.disconnect();
  }, []);

  useEffect(() => {
    const onMouseDown = (e) => {
      if (!showPhoneConsent) return;
      if (phoneConsentRef.current && !phoneConsentRef.current.contains(e.target)) {
        setShowPhoneConsent(false);
      }
    };

    const onKeyDown = (e) => {
      if (!showPhoneConsent) return;
      if (e.key === 'Escape') setShowPhoneConsent(false);
    };

    document.addEventListener('mousedown', onMouseDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onMouseDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [showPhoneConsent]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!captchaToken) {
      setStatus('⚠️ Please complete the CAPTCHA.');
      return;
    }
    setIsLoading(true);

    try {
      const payload = {
        ...form,
        captcha_token: captchaToken,
        parent_url_field: getParentUrl(),
      };
      const resp = await fetch(`${API_URL}/quick-intake/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!resp.ok) throw new Error('Submission failed!');
      setIsLoading(false);
      setSubmitted(true);
      setStatus();
    } catch (err) {
      console.error(err);
      setIsLoading(false);
      setStatus('❌ Submission error. Please try again.');
    }
  };

  return (
    <div
      ref={containerRef}
      className="relative max-w-xl mx-auto bg-white p-8 rounded-2xl shadow-lg"
    >
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* all existing form fields */}
        <h2 className="text-3xl font-bold text-gray-800 text-center">
          Request a Consultation
        </h2>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-1">
          {[
            { key: 'first_name', label: 'First Name' },
            { key: 'last_name', label: 'Last Name' },
            { key: 'phone', label: 'Phone' },
            { key: 'email', label: 'Email' },
          ].map(({ key, label }) => (
            <div key={key} className="flex flex-col">
              <div className="flex items-center gap-1">
                <label htmlFor={key} className="text-sm font-medium text-gray-700 mb-0">
                  {label}
                </label>

                {key === 'phone' && (
                  <span className="relative" ref={phoneConsentRef}>
                    <button
                      type="button"
                      aria-label="Text message consent information"
                      aria-expanded={showPhoneConsent}
                      onClick={() => setShowPhoneConsent((v) => !v)}
                      className="ml-0.5 text-indigo-600 hover:text-indigo-800 font-bold leading-none"
                    >
                      *
                    </button>

                    {showPhoneConsent && (
                      <div
                        role="tooltip"
                        className="absolute left-0 top-6 w-72 rounded-lg bg-gray-900 text-white text-xs p-3 shadow-lg ring-1 ring-white/10 z-20"
                      >
                        By submitting, you agree to receive text message notifications.
                        <div className="absolute -top-1 left-3 w-2 h-2 bg-gray-900 rotate-45" />
                      </div>
                    )}
                  </span>
                )}
              </div>
              <input
                id={key}
                name={key}
                type={key === 'email' ? 'email' : key === 'phone' ? 'tel' : 'text'}
                inputMode={key === 'phone' ? 'numeric' : undefined}
                required={key !== 'middle_name'}
                value={form[key]}
                onChange={(e) => {
                  let val = e.target.value;
                  if (key === 'phone') {
                    val = formatPhone(val);
                  }
                  setForm({ ...form, [key]: val });
                }}
                className="px-3 py-1 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 transition"
              />
            </div>
          ))}
        </div>

        <div className="flex flex-col">
          <label htmlFor="message" className="text-sm font-medium text-gray-700 mb-1">
            Message (optional)
          </label>
          <textarea
            id="message"
            name="message"
            rows={4}
            value={form.message}
            onChange={(e) => setForm({ ...form, message: e.target.value })}
            className="px-3 py-1 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 transition"
          />
        </div>

        <div className="flex justify-center">
          <ReCAPTCHA
            sitekey={SITE_KEY_V2}
            onChange={(token) => {
              setCaptchaToken(token);
              setStatus('');
            }}
          />
        </div>

        <button
          type="submit"
          disabled={!captchaToken}
          className="w-full py-3 bg-gradient-to-r from-indigo-500 to-indigo-700 hover:from-indigo-600 hover:to-indigo-800 text-white font-semibold rounded-xl shadow-md transition disabled:opacity-50"
        >
          Send Message
        </button>

        {status && (
          <p
            className={`mt-4 text-center text-sm ${
              status.startsWith('✅')
                ? 'text-green-600'
                : status.startsWith('⚠️')
                ? 'text-yellow-600'
                : 'text-red-600'
            }`}
          >
            {status}
          </p>
        )}
      </form>

      {isLoading && !submitted && (
        <div className="absolute inset-0 bg-white bg-opacity-75 flex items-center justify-center z-10">
          <div className="w-12 h-12 border-4 border-gray-200 border-t-indigo-500 rounded-full animate-spin" />
        </div>
      )}
      {submitted && (
        <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center z-20">
          <div className="bg-white p-8 rounded-lg shadow-lg max-w-sm h-64 flex flex-col items-center justify-center space-y-4">
            <svg
              className="w-16 h-16 text-green-500 animate-bounce"
              fill="none"
              stroke="currentColor"
              strokeWidth={2}
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
            </svg>
            <h3 className="text-2xl font-semibold text-gray-800 text-center">
                Thanks for the message!
            </h3>
            <p className="text-sm italic text-gray-700 text-center px-4">
                Check your email for the next steps
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
